import React, { useState, useEffect } from 'react'
import {
  BookOpen, ChevronRight, CheckCircle, Lightbulb,
  Play, Target, Brain, ArrowRight, Star, AlertTriangle,
  Calculator, BarChart2, FileText, Layers, PenTool, Lock,
  Youtube
} from 'lucide-react'
import { useProgressStore, useLearnProgress } from '../store/progressStore'
import { useVideoStore } from '../store/videoStore'
import ConceptVideoModal from '../components/ConceptVideoModal'

// ── 10단계 학습 데이터 ────────────────────────────────────────────────────────
const STEPS = [
  {
    id: 'FS01',
    order: 1,
    title: '일차방정식과 직선의 관계',
    subtitle: 'ax + by + c = 0 이 직선이 되는 원리',
    difficulty: 'beginner' as const,
    teaches: ['LinearEquation', 'Graph', 'TwoVariables'],
    icon: '📏',
    color: { bg: 'bg-blue-50', border: 'border-blue-200', badge: 'bg-blue-100 text-blue-700', accent: 'text-blue-600', btn: 'bg-blue-500' },
    objective: '미지수 2개인 일차방정식 ax+by+c=0의 그래프가 직선임을 이해하고, y=ax+b 꼴로 변환할 수 있다.',
    concept: `미지수가 2개인 일차방정식 ax + by + c = 0 의 그래프는 직선입니다.

📌 핵심 원리
• x, y 두 미지수를 만족하는 순서쌍 (x, y)는 무한히 많다
• 이 점들을 좌표평면에 찍으면 → 직선이 된다!
• 직선의 방정식 = 일차방정식

📐 y = ax + b 꼴로 변환 (b ≠ 0 일 때)
  ax + by + c = 0
  by = −ax − c
  y = −(a/b)x − (c/b)

예) 2x + 3y − 6 = 0
  3y = −2x + 6
  y = −(2/3)x + 2
  → 기울기 −2/3, y절편 2인 직선

📌 특수한 경우
• b = 0 이면 ax + c = 0 → x = −c/a → y축에 평행한 수직선
• a = 0 이면 by + c = 0 → y = −c/b → x축에 평행한 수평선`,
    examples: [
      { q: 'x + 2y − 4 = 0 을 y = ax + b 꼴로 변환하세요.', a: 'y = −(1/2)x + 2', detail: '2y = −x + 4 → y = −(1/2)x + 2. 기울기 −1/2, y절편 2' },
      { q: '3x − y + 1 = 0 의 기울기와 y절편은?', a: '기울기 3, y절편 1', detail: '−y = −3x − 1 → y = 3x + 1. 기울기 a=3, y절편 b=1' },
      { q: '방정식 x = 3 의 그래프는?', a: 'x = 3 인 수직선 (y축에 평행)', detail: 'b=0인 경우: 어떤 y값을 가져도 x는 항상 3. 세로 직선.' },
    ],
    keyPoints: ['ax+by+c=0의 해집합 → 직선', 'b≠0: y=ax+b로 변환 가능', 'b=0: 수직선(x=k), a=0: 수평선(y=k)'],
    practice: {
      q: '2x − 3y + 9 = 0 을 y = ax + b 꼴로 변환하고, 기울기와 y절편을 구하세요.',
      a: 'y = (2/3)x + 3 | 기울기: 2/3, y절편: 3',
      hint: '−3y = −2x − 9 → 양변을 −3으로 나누기',
      solutionSteps: [
        { label: '① y 항을 좌변으로', content: '2x − 3y + 9 = 0 → −3y = −2x − 9' },
        { label: '② 양변을 −3으로 나누기', content: 'y = (2/3)x + 3  (음수로 나누므로 부호 주의!)' },
        { label: '③ 기울기·y절편 읽기', content: '기울기 a = 2/3, y절편 b = 3' },
      ]
    },
    misconceptions: [
      { code: 'F01', desc: '−3y = −2x − 9 에서 −3으로 나눌 때 부호를 틀린다 → 음수로 나누면 분자 부호도 반전!' },
    ],
  },
  {
    id: 'FS02',
    order: 2,
    title: '기울기·절편으로 직선 결정',
    subtitle: '두 조건이 직선 하나를 완전히 결정한다',
    difficulty: 'beginner' as const,
    teaches: ['Slope', 'Intercept', 'UniquenesOfLine'],
    icon: '📌',
    color: { bg: 'bg-indigo-50', border: 'border-indigo-200', badge: 'bg-indigo-100 text-indigo-700', accent: 'text-indigo-600', btn: 'bg-indigo-500' },
    objective: '기울기와 한 점, 또는 두 점으로 직선이 유일하게 결정됨을 이해하고 방정식을 구할 수 있다.',
    concept: `직선은 다음 두 가지 정보가 있으면 하나로 결정됩니다.

📌 직선을 결정하는 조건
① 기울기 + y절편 → y = ax + b 즉시 완성
② 기울기 + 한 점   → y = ax + b 에 점 대입 → b 결정
③ 두 점            → 기울기 계산 후 ②로 해결

📌 방정식 형태 비교
• 함수형: y = ax + b (기울기와 y절편이 명확)
• 방정식형: ax + by + c = 0 (일반형)
  → 두 형태는 서로 변환 가능!

📌 그래프 그리는 법
x절편: y=0 대입 → 점(k, 0)
y절편: x=0 대입 → 점(0, m)
→ 두 절편점을 이어 직선 그리기

⚠️ 직선은 반드시 두 점(조건)이 있어야 결정!
기울기만 알면? → 평행한 직선이 무한히 존재`,
    examples: [
      { q: '기울기 2이고 점 (1, 3)을 지나는 직선의 방정식을 ax+by+c=0 꼴로 나타내세요.', a: '2x − y + 1 = 0', detail: 'y=2x+b, 3=2+b→b=1, y=2x+1 → 2x−y+1=0' },
      { q: '두 점 (0, 4)와 (2, 0)을 지나는 직선의 방정식은?', a: '2x + y − 4 = 0', detail: '기울기=(0−4)/(2−0)=−2, y절편=4 → y=−2x+4 → 2x+y−4=0' },
    ],
    keyPoints: ['기울기+한 점 → 유일한 직선', '두 점 → 기울기 먼저 계산', 'y=ax+b ↔ ax−y+b=0 변환'],
    practice: {
      q: '두 점 (−1, 5)와 (3, −3)을 지나는 직선을 ax+by+c=0 꼴로 나타내세요.',
      a: '2x + y − 3 = 0',
      hint: '기울기=(−3−5)/(3−(−1))=−8/4=−2, y절편: 5=−2(−1)+b → b=3',
      solutionSteps: [
        { label: '① 기울기 계산', content: 'a = (−3−5)/(3−(−1)) = −8/4 = −2' },
        { label: '② y절편 계산', content: 'y=−2x+b에 (−1,5) 대입: 5=2+b → b=3' },
        { label: '③ y=ax+b 형태', content: 'y = −2x + 3' },
        { label: '④ 일반형 변환', content: '2x + y − 3 = 0' },
      ]
    },
    misconceptions: [
      { code: 'F02', desc: 'y=−2x+3을 일반형으로 변환할 때 2x−y+3=0으로 쓰는 실수 → y를 이항하면 2x−y+3=0이 아니라 2x+y−3=0!' },
    ],
  },
  {
    id: 'FS03',
    order: 3,
    title: '두 직선의 위치 관계',
    subtitle: '평행·일치·교차, 기울기로 한 번에 판단',
    difficulty: 'beginner' as const,
    teaches: ['ParallelLines', 'CoincidentLines', 'Intersection'],
    icon: '↔️',
    color: { bg: 'bg-emerald-50', border: 'border-emerald-200', badge: 'bg-emerald-100 text-emerald-700', accent: 'text-emerald-600', btn: 'bg-emerald-500' },
    objective: '두 직선의 기울기와 y절편을 비교하여 위치 관계(평행·일치·교차)를 판단할 수 있다.',
    concept: `두 직선 y = a₁x + b₁  과  y = a₂x + b₂ 의 위치 관계

📌 세 가지 경우
① 한 점에서 교차: a₁ ≠ a₂  (기울기가 다름)
  → 교점이 정확히 하나 존재

② 평행 (만나지 않음): a₁ = a₂, b₁ ≠ b₂
  → 기울기 같고, y절편 다름
  → 교점 없음

③ 일치 (완전히 같은 직선): a₁ = a₂, b₁ = b₂
  → 모든 점이 교점 (무수히 많은 교점)

📌 판별 요약표
┌────────────┬────────────────────────────┐
│ 교차       │ a₁ ≠ a₂                    │
│ 평행       │ a₁ = a₂, b₁ ≠ b₂          │
│ 일치       │ a₁ = a₂, b₁ = b₂          │
└────────────┴────────────────────────────┘`,
    examples: [
      { q: 'y = 3x + 1 과 y = 3x − 2 의 위치 관계는?', a: '평행 (기울기 같고, y절편 다름 1≠−2)', detail: '기울기 모두 3으로 같지만 y절편이 다르므로 평행. 교점 없음.' },
      { q: 'y = 2x + 4 와 2y = 4x + 8 의 위치 관계는?', a: '일치 (같은 직선)', detail: '2y=4x+8 → y=2x+4. 두 식이 완전히 같으므로 일치.' },
      { q: 'y = 5x − 3 과 y = −2x + 1 의 위치 관계는?', a: '교차 (기울기 5≠−2)', detail: '기울기가 다르므로 반드시 한 점에서 만남.' },
    ],
    keyPoints: ['기울기 다름 → 교차', '기울기 같고 y절편 다름 → 평행', '기울기·y절편 모두 같음 → 일치'],
    practice: {
      q: '다음 두 직선의 위치 관계를 각각 구하세요.\n① y = 4x + 2  와  y = 4x − 5\n② y = 3x + 1  와  y = −3x + 1',
      a: '① 평행 (기울기 4=4, y절편 2≠−5)\n② 교차 (기울기 3≠−3)',
      hint: '기울기부터 비교! 같으면 y절편 비교.',
      solutionSteps: [
        { label: '① 기울기 비교', content: '4 = 4 (같음) → y절편 비교: 2 ≠ −5 → 평행' },
        { label: '② 기울기 비교', content: '3 ≠ −3 → 바로 교차 판정' },
      ]
    },
    misconceptions: [
      { code: 'F03', desc: '"기울기가 같으면 무조건 평행"이라고 생각한다 → 기울기+y절편 모두 같으면 일치!'},
    ],
  },
  {
    id: 'FS04',
    order: 4,
    title: '연립방정식과 교점',
    subtitle: '두 직선의 교점 = 연립방정식의 해',
    difficulty: 'intermediate' as const,
    teaches: ['SimultaneousEquations', 'Intersection', 'Solution'],
    icon: '🎯',
    color: { bg: 'bg-yellow-50', border: 'border-yellow-200', badge: 'bg-yellow-100 text-yellow-700', accent: 'text-yellow-600', btn: 'bg-yellow-500' },
    objective: '두 직선의 교점의 좌표가 연립방정식의 해임을 이해하고, 대입법·가감법으로 교점을 구할 수 있다.',
    concept: `두 직선의 교점 = 연립방정식의 해

📌 핵심 연결
• 직선 ①: y = 2x + 1
• 직선 ②: y = −x + 4
• 교점: 두 직선이 동시에 만족하는 (x, y)
  → 연립방정식을 풀면 교점 좌표!

📌 대입법으로 교점 구하기
  ①=② : 2x + 1 = −x + 4
  3x = 3 → x = 1
  y = 2(1) + 1 = 3
  교점: (1, 3)

📌 가감법으로 교점 구하기
  y = 2x + 1  →  2x − y = −1
  y = −x + 4  →   x + y =  4
  더하면: 3x = 3 → x=1, y=3

📌 검증 필수!
  구한 (x, y)를 두 방정식에 모두 대입해서 확인`,
    examples: [
      { q: 'y = 3x − 2 와 y = x + 4 의 교점은?', a: '(3, 7)', detail: '3x−2=x+4 → 2x=6 → x=3, y=7. 검증: 3(3)−2=7 ✓, 3+4=7 ✓' },
      { q: 'x + y = 5 와 2x − y = 1 의 교점은?', a: '(2, 3)', detail: '가감법: 더하면 3x=6 → x=2, y=3' },
    ],
    keyPoints: ['교점 = 연립방정식의 해', '대입법: y=식을 다른 식에 대입', '가감법: 두 식을 더하거나 빼서 소거'],
    practice: {
      q: '두 직선 y = 2x − 1 과 y = −x + 5 의 교점을 구하세요.',
      a: '교점: (2, 3)',
      hint: '두 식을 같다고 놓으세요: 2x−1 = −x+5',
      solutionSteps: [
        { label: '① 대입법 설정', content: '2x − 1 = −x + 5' },
        { label: '② x 계산', content: '3x = 6 → x = 2' },
        { label: '③ y 계산', content: 'y = 2(2) − 1 = 3' },
        { label: '④ 검증', content: '−x+5: −2+5=3 ✓ → 교점 (2, 3)' },
      ]
    },
    misconceptions: [
      { code: 'F04', desc: '교점을 구한 후 검증하지 않아 계산 실수를 놓친다 → 반드시 두 식 모두에 대입해 확인!' },
    ],
  },
  {
    id: 'FS05',
    order: 5,
    title: '그래프로 연립방정식 풀기',
    subtitle: '두 직선을 그려 교점으로 해를 찾는다',
    difficulty: 'intermediate' as const,
    teaches: ['GraphicalSolution', 'SimultaneousEquations', 'Visualization'],
    icon: '📉',
    color: { bg: 'bg-orange-50', border: 'border-orange-200', badge: 'bg-orange-100 text-orange-700', accent: 'text-orange-600', btn: 'bg-orange-500' },
    objective: '두 일차방정식의 그래프를 그리고, 교점을 읽어 연립방정식의 해를 구할 수 있다.',
    concept: `그래프를 이용한 연립방정식 풀이 순서

📌 4단계 절차
① 각 방정식을 y = ax + b 꼴로 변환
② 각 직선의 x절편, y절편을 구한다
③ 두 직선을 좌표평면에 그린다
④ 교점의 좌표를 읽는다 → 이것이 해!

예) { x + y = 4
    { 2x − y = 2

① y = −x + 4  (x절편:4, y절편:4)
   y = 2x − 2  (x절편:1, y절편:−2)
② 두 직선 그리기
③ 교점 읽기: (2, 2)
④ 해: x=2, y=2

📌 그래프 해법의 특징
• 장점: 해의 개수와 위치를 시각적으로 확인
• 단점: 정수가 아닌 해는 정확히 읽기 어려움
• 검산 필수: 교점 좌표를 두 식에 대입 확인`,
    examples: [
      { q: '{ y = x + 2, y = −2x + 5 의 그래프 교점은?', a: '(1, 3)', detail: 'x+2=−2x+5 → 3x=3 → x=1, y=3. 그래프에서 (1,3) 확인.' },
      { q: '{ x + 2y = 6, x − y = 0 의 그래프 교점은?', a: '(2, 2)', detail: '①: y=−(1/2)x+3, ②: y=x. 교점: x=−(1/2)x+3 → x=2, y=2' },
    ],
    keyPoints: ['각 방정식 → y=ax+b 변환', 'x·y절편으로 두 직선 그리기', '교점 읽기 = 연립방정식의 해'],
    practice: {
      q: '{ 2x + y = 7, x − y = 2 를 그래프로 풀어 해를 구하세요.',
      a: 'x = 3, y = 1 (교점: (3, 1))',
      hint: '①: y=−2x+7 (x절편3.5, y절편7), ②: y=x−2 (x절편2, y절편−2)',
      solutionSteps: [
        { label: '① 각 식 변환', content: '①: y=−2x+7  ②: y=x−2' },
        { label: '② 절편으로 각 직선 그리기', content: '①: (0,7),(3.5,0) / ②: (0,−2),(2,0)' },
        { label: '③ 교점 확인 (대입)', content: '−2x+7=x−2 → 3x=9 → x=3, y=1' },
        { label: '④ 해', content: 'x=3, y=1 | 교점 (3,1)' },
      ]
    },
    misconceptions: [
      { code: 'F05', desc: '그래프를 대략 그리고 교점을 어림잡아 읽는다 → 반드시 계산으로 정확한 교점을 구해야 함!' },
    ],
  },
  {
    id: 'FS06',
    order: 6,
    title: '해의 특수한 경우',
    subtitle: '평행하면 해 없음, 일치하면 무수히 많은 해',
    difficulty: 'intermediate' as const,
    teaches: ['NoSolution', 'InfiniteSolutions', 'ParallelLines', 'Coincident'],
    icon: '∞',
    color: { bg: 'bg-rose-50', border: 'border-rose-200', badge: 'bg-rose-100 text-rose-700', accent: 'text-rose-600', btn: 'bg-rose-500' },
    objective: '연립방정식의 해가 없는 경우와 무수히 많은 경우를 그래프와 계산으로 판별할 수 있다.',
    concept: `연립방정식 해의 세 가지 경우

📌 경우 1: 해가 유일 (한 점에서 교차)
  기울기가 다른 두 직선 → 교점 1개 → 해 1쌍

📌 경우 2: 해가 없음 (불능, 두 직선 평행)
  기울기 같고, y절편 다름
  → 두 직선이 만나지 않음 → 해 없음
  풀이하면: 모순 (예: 0 = 5)이 나옴

📌 경우 3: 무수히 많은 해 (부정, 두 직선 일치)
  기울기·y절편 모두 같음
  → 같은 직선 → 무수히 많은 교점 → 해 무한
  풀이하면: 항등식 (예: 0 = 0)이 나옴

예시 — 해 없음:
  { y = 2x + 1
  { y = 2x + 3
  → 2x+1 = 2x+3 → 1 = 3 (모순!) → 해 없음

예시 — 무수히 많은 해:
  { y = 2x + 1
  { 2y = 4x + 2  (→ y = 2x + 1)
  → 같은 직선 → 해 무한`,
    examples: [
      { q: '{ 3x − y = 2, 6x − 2y = 4 의 해는?', a: '무수히 많은 해 (두 직선 일치)', detail: '②÷2: 3x−y=2 → 첫 번째와 동일. 같은 직선.' },
      { q: '{ x + 2y = 3, 2x + 4y = 8 의 해는?', a: '해 없음 (두 직선 평행)', detail: '②÷2: x+2y=4. 첫 번째: x+2y=3. 우변이 다름 → 평행 → 해 없음' },
    ],
    keyPoints: ['모순(0=k, k≠0) → 해 없음(평행)', '항등(0=0) → 무수히 많은 해(일치)', '기울기 비교로 빠른 판별 가능'],
    practice: {
      q: '다음 각 연립방정식의 해의 종류를 판별하세요.\n① { 2x + y = 5, 4x + 2y = 10\n② { x − 3y = 2, 2x − 6y = 5',
      a: '① 무수히 많은 해 (일치)\n② 해 없음 (평행)',
      hint: '①: ②÷2를 해봐요. ②: ②÷2를 해봐요.',
      solutionSteps: [
        { label: '① ②÷2', content: '2x+y=5 → ①과 동일 → 같은 직선 → 무수히 많은 해' },
        { label: '② ②÷2', content: 'x−3y=2.5. ①은 x−3y=2. 우변 2≠2.5 → 평행 → 해 없음' },
      ]
    },
    misconceptions: [
      { code: 'F06', desc: '"해가 없으면 틀린 문제"라고 생각한다 → 해 없음·무수히 많음도 정상적인 수학적 결과!' },
    ],
  },
  {
    id: 'FS07',
    order: 7,
    title: '계수 조건과 해의 종류',
    subtitle: '미지수 포함 계수에서 해의 조건 찾기',
    difficulty: 'intermediate' as const,
    teaches: ['ParameterCondition', 'SolutionType', 'Ratio'],
    icon: '🔣',
    color: { bg: 'bg-cyan-50', border: 'border-cyan-200', badge: 'bg-cyan-100 text-cyan-700', accent: 'text-cyan-600', btn: 'bg-cyan-500' },
    objective: '미지수 k가 포함된 연립방정식에서 해의 종류에 따른 k의 조건을 구할 수 있다.',
    concept: `연립방정식 { a₁x + b₁y = c₁  의 해 조건
              { a₂x + b₂y = c₂

📌 비율로 판별
a₁/a₂ = b₁/b₂ = c₁/c₂  → 일치 (무수히 많은 해)
a₁/a₂ = b₁/b₂ ≠ c₁/c₂  → 평행 (해 없음)
a₁/a₂ ≠ b₁/b₂           → 교차 (유일한 해)

예) { 2x + ky = 4
    { x + 3y = 2

a비율: 2/1 = 2  b비율: k/3  c비율: 4/2 = 2

• 유일한 해: k/3 ≠ 2 → k ≠ 6
• 무수히 많은 해: k/3 = 2 이고 c비율도 2 → k = 6 (이미 c비율=2이므로 무한해)
• 해 없음: k = 6이고 c₁/c₂ ≠ a₁/a₂이어야 하는데 여기서는 4/2=2이므로 해당 없음

핵심: k/3 = 2 → k = 6일 때 무수히 많은 해
      k ≠ 6일 때 유일한 해`,
    examples: [
      { q: '{ kx + y = 3, 2x + y = 4 가 해가 없으려면 k는?', a: 'k = 2 (단, 3/4 ≠ 1이므로 평행)', detail: 'k/2=1/1 → k=2. c비율: 3/4≠1 → 평행 → 해 없음' },
      { q: '{ x + 2y = k, 2x + 4y = 8 이 무수히 많은 해를 가지려면?', a: 'k = 4', detail: '2÷1=2, 4÷2=2, 8÷k=2 → k=4' },
    ],
    keyPoints: ['a비율·b비율·c비율 모두 같음 → 일치', 'a·b비율 같고 c비율 다름 → 평행', 'a·b비율 다름 → 유일한 해'],
    practice: {
      q: '{ 3x + ay = 6, x + 2y = b 가 무수히 많은 해를 가지려면 a, b의 값은?',
      a: 'a = 6, b = 2',
      hint: '계수 비율: 3/1 = a/2 = 6/b 모두 같아야 함',
      solutionSteps: [
        { label: '① a 구하기', content: '3/1 = a/2 → a = 6' },
        { label: '② b 구하기', content: '3/1 = 6/b → b = 2' },
        { label: '③ 확인', content: '3:1:6=6:2:2 → 모든 비율 3으로 같음 → 일치' },
      ]
    },
    misconceptions: [
      { code: 'F07', desc: 'a비율만 확인하고 c비율을 빠뜨린다 → 세 쌍(a, b, c)의 비율을 모두 확인해야 함!' },
    ],
  },
  {
    id: 'FS08',
    order: 8,
    title: '실생활 연립방정식 활용',
    subtitle: '두 조건이 있는 문제를 연립방정식으로!',
    difficulty: 'intermediate' as const,
    teaches: ['WordProblem', 'Modeling', 'SystemOfEquations'],
    icon: '🌍',
    color: { bg: 'bg-amber-50', border: 'border-amber-200', badge: 'bg-amber-100 text-amber-700', accent: 'text-amber-600', btn: 'bg-amber-500' },
    objective: '실생활 두 조건을 연립방정식으로 세우고 풀어 결과를 해석할 수 있다.',
    concept: `실생활 연립방정식 활용 4단계

① 미지수 설정: 구하는 두 양을 x, y로 놓기
② 연립방정식 세우기:
   조건 1 → 방정식 ①
   조건 2 → 방정식 ②
③ 연립방정식 풀기 (대입법·가감법)
④ 검토: 구한 해가 문제 맥락에 맞는지 확인
   (자연수·양수 조건, 단위 등)

자주 나오는 유형:
• 가격·개수: 총금액 + 총개수 조건
• 속력·거리·시간: 거리=속력×시간
• 두 수의 합·차: x+y=합, x−y=차
• 농도 혼합: 소금양=농도×전체량`,
    examples: [
      { q: '두 수의 합이 20이고 차가 6일 때 두 수를 구하세요.', a: 'x=13, y=7', detail: 'x+y=20, x−y=6 → 가감법: 2x=26 → x=13, y=7' },
      { q: '어른 2명과 어린이 3명의 입장료가 13,000원, 어른 1명과 어린이 4명이 11,000원이면 각각의 입장료는?', a: '어른 5,000원, 어린이 1,000원', detail: '2a+3c=13000, a+4c=11000. ①−②×2: −5c=−9000 → c=1000 (아님, 풀이 확인 필요)\n①: a+4c=11000 → ②×2: 2a+8c=22000. ①−②×2: −5c=−9000? 다시: ①−②: a−c=2000 → a=c+2000. ②: c+2000+4c=11000 → 5c=9000 → c=1800, a=3800 (아님)\n정확히: 2a+3c=13000 ①, a+4c=11000 ② → ①−②×2: −5c=−9000 → c=1800, a=11000−7200=3800' },
    ],
    keyPoints: ['미지수 2개 → 조건 2개 필요', '가격문제: 각 항목의 합=총액', '두 수 문제: 합·차·배수 관계'],
    practice: {
      q: '농구공 x개와 축구공 y개를 합쳐 15개 샀다. 농구공은 30,000원, 축구공은 20,000원이고 총 380,000원을 냈다. x, y를 구하세요.',
      a: 'x = 8, y = 7',
      hint: 'x+y=15, 30000x+20000y=380000',
      solutionSteps: [
        { label: '① 연립방정식 설정', content: 'x+y=15  ①\n30000x+20000y=380000  ②' },
        { label: '② ②÷10000 단순화', content: '3x+2y=38  ②′' },
        { label: '③ ①×2: 2x+2y=30, ②′−(①×2)', content: 'x=8' },
        { label: '④ y 계산', content: 'y=15−8=7' },
      ]
    },
    misconceptions: [
      { code: 'F08', desc: '단위(원, 개 등)를 통일하지 않고 방정식을 세운다 → 같은 단위끼리만 등호로 연결!' },
    ],
  },
  {
    id: 'FS09',
    order: 9,
    title: '미지수 포함 직선 결정',
    subtitle: '조건에 맞는 미지수 k 값 찾기',
    difficulty: 'advanced' as const,
    teaches: ['ParameterLine', 'Condition', 'Substitution'],
    icon: '🔑',
    color: { bg: 'bg-violet-50', border: 'border-violet-200', badge: 'bg-violet-100 text-violet-700', accent: 'text-violet-600', btn: 'bg-violet-500' },
    objective: '직선에 미지수 k가 포함된 경우, 주어진 조건(점 통과·평행·수직 등)으로 k를 구할 수 있다.',
    concept: `미지수 k가 있는 직선 문제 유형

📌 유형 1: 특정 점을 지나는 조건
  직선 y = kx + 2 가 점 (3, 8)을 지나면?
  8 = 3k + 2 → k = 2

📌 유형 2: 두 직선이 평행한 조건
  y = kx + 1 ∥ y = 3x − 2
  → 기울기 같음: k = 3 (y절편 1≠−2이므로 평행 확인)

📌 유형 3: 두 직선이 수직인 조건
  기울기의 곱 = −1
  y = kx + 1 ⊥ y = 2x + 3
  → k × 2 = −1 → k = −1/2

📌 유형 4: x절편·y절편 조건
  직선이 x절편 = 4, y절편 = 3 이면?
  두 점 (4, 0)과 (0, 3) 이용
  기울기 = (3−0)/(0−4) = −3/4
  y = −(3/4)x + 3`,
    examples: [
      { q: '직선 y = kx − 1 이 점 (2, 5)를 지날 때 k는?', a: 'k = 3', detail: '5 = 2k − 1 → 2k = 6 → k = 3' },
      { q: '직선 y = kx + 2 가 y = −(1/2)x + 1 에 수직이면 k는?', a: 'k = 2', detail: '수직조건: k × (−1/2) = −1 → k = 2' },
    ],
    keyPoints: ['점 통과: 좌표 대입 → k 계산', '평행: 기울기 같고 y절편 다름', '수직: 기울기 곱 = −1'],
    practice: {
      q: '직선 y = kx + 3 이 직선 y = 4x − 1 과 평행하고, 점 (a, 7)을 지날 때 a의 값을 구하세요.',
      a: 'k = 4, a = 1',
      hint: '평행 → k=4. 점 대입: 7=4a+3',
      solutionSteps: [
        { label: '① k 결정 (평행 조건)', content: 'k = 4 (기울기 같음, y절편 3≠−1 확인)' },
        { label: '② 점 대입', content: 'y = 4x + 3 에 (a, 7) 대입: 7 = 4a + 3' },
        { label: '③ a 계산', content: '4a = 4 → a = 1' },
      ]
    },
    misconceptions: [
      { code: 'F09', desc: '두 직선이 수직이면 기울기의 합이 0이라고 생각한다 → 수직 조건은 기울기의 곱 = −1!' },
    ],
  },
  {
    id: 'FS10',
    order: 10,
    title: '종합 오답 분석',
    subtitle: '일차함수·방정식의 핵심 오개념 총정리',
    difficulty: 'advanced' as const,
    teaches: ['Misconception', 'Review', 'MasteryCheck'],
    icon: '🎓',
    color: { bg: 'bg-slate-50', border: 'border-slate-300', badge: 'bg-slate-100 text-slate-700', accent: 'text-slate-700', btn: 'bg-slate-600' },
    objective: '일차함수와 일차방정식에서 자주 발생하는 오개념을 진단하고 올바른 개념으로 교정할 수 있다.',
    concept: `일차함수와 일차방정식 핵심 오개념 9가지

F01 변환 부호 오류
잘못: ax+by=c → y=ax/b+c/b 로 계산
올바름: by=−ax+c → y=−(a/b)x+(c/b)

F02 일반형 변환 실수
잘못: y=2x+3 → 2x−y+3=0
올바름: y=2x+3 → 2x−y+3=0 이 맞음! (방향 확인)

F03 평행·일치 혼동
잘못: 기울기 같으면 무조건 평행
올바름: y절편도 같으면 일치, 다르면 평행

F04 교점 검증 생략
잘못: 한 식에만 대입해서 확인
올바름: 두 식 모두 대입 필수

F05 그래프 어림 읽기
잘못: 대략적으로 교점을 추측
올바름: 계산으로 정확한 교점 결정

F06 해의 특수성 이해 부족
잘못: 해 없음 = 문제가 틀림
올바름: 평행이면 해 없음도 정상 결과

F07 계수 비율 일부만 확인
잘못: a, b 비율만 보고 c 비율 생략
올바름: a:b:c 세 쌍 비율 모두 확인

F08 단위 불일치
잘못: 원과 천원을 섞어 방정식 구성
올바름: 방정식 내 단위 통일 필수

F09 수직 조건 오류
잘못: 수직이면 기울기 합 = 0
올바름: 수직이면 기울기 곱 = −1`,
    examples: [
      { q: 'y = 3x − 2 를 일반형으로 나타내면?', a: '3x − y − 2 = 0', detail: 'y 이항: 3x − y − 2 = 0. 일반형: ax + by + c = 0 꼴' },
      { q: '{ 2x + ky = 4, x + 3y = 2 가 해가 없으려면 k = ?', a: 'k = 6', detail: '기울기비: 2/1 = k/3 → k=6. 상수비: 4/2=2 ≠ k/3=2는 틀림. 재확인: 2/1=k/3 → k=6, 4/2=2=2이므로 일치!? 실제로는 무수히 많은 해. 해 없으려면: k=6이고 c비율이 달라야 함. 이 문제에서 c비율=2=2이므로 k=6이면 일치. k≠6이면 유일한 해.' },
    ],
    keyPoints: ['F01~F09 오개념 진단', 'ax+by+c=0 변환 정확히', '수직·평행·일치 판별 공식 암기'],
    practice: {
      q: '종합 문제: 직선 l₁: 2x + y − 4 = 0 과 l₂: x − y + 1 = 0 에 대해\n① 각 직선의 기울기와 y절편\n② 두 직선의 위치 관계\n③ 교점 좌표를 구하세요.',
      a: '① l₁: 기울기−2, y절편4 / l₂: 기울기1, y절편1\n② 기울기 −2≠1 → 교차\n③ 교점 (1, 2)',
      hint: '① 각각 y=ax+b로 변환 ② 기울기 비교 ③ 연립방정식 풀기',
      solutionSteps: [
        { label: '① l₁ 변환', content: 'y = −2x + 4 (기울기−2, y절편4)' },
        { label: '① l₂ 변환', content: 'y = x + 1 (기울기1, y절편1)' },
        { label: '② 위치 관계', content: '−2 ≠ 1 → 교차 (유일한 교점)' },
        { label: '③ 교점', content: '−2x+4 = x+1 → −3x=−3 → x=1, y=2 → (1,2)' },
      ]
    },
    misconceptions: [
      { code: 'F01', desc: '일반형 → y=ax+b 변환 시 부호 실수' },
      { code: 'F03', desc: '평행과 일치 혼동' },
      { code: 'F09', desc: '수직 조건: 기울기 곱=−1 적용 오류' },
    ],
  },
]

const DIFFICULTY_CONFIG = {
  beginner: { label: '초급', color: 'bg-emerald-100 text-emerald-700' },
  intermediate: { label: '중급', color: 'bg-orange-100 text-orange-700' },
  advanced: { label: '고급', color: 'bg-red-100 text-red-700' },
}

// ── 연습문제 컴포넌트 ──────────────────────────────────────────────────────────
function PracticeBox({
  practice, stepId, unitId, alreadyAnswered, onAnswered,
}: {
  practice: { q: string; a: string; hint: string; solutionSteps?: { label: string; content: string }[] }
  stepId: string
  unitId: string
  alreadyAnswered: boolean
  onAnswered: () => void
}) {
  const [showHint, setShowHint] = useState(false)
  const [showAnswer, setShowAnswer] = useState(alreadyAnswered)
  const [showSteps, setShowSteps] = useState(false)
  const markPractice = useProgressStore(s => s.markPracticeAnswered)

  const solutionSteps = practice.solutionSteps ?? [
    { label: '① 핵심 공식 확인', content: practice.hint },
    { label: '② 풀이 과정', content: '힌트를 이용해 단계적으로 계산합니다.' },
    { label: '③ 최종 답', content: practice.a },
  ]

  const handleShowAnswer = () => {
    setShowAnswer(true)
    if (!alreadyAnswered) {
      markPractice(unitId, stepId)
      onAnswered()
    }
  }

  return (
    <div className={`rounded-xl border-2 p-4 transition-all ${
      alreadyAnswered ? 'border-emerald-300 bg-emerald-50/60' : 'border-orange-200 bg-orange-50'
    }`}>
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs font-bold text-orange-600 flex items-center gap-1.5">
          <Play size={12} /> 연습 문제
        </p>
        {alreadyAnswered && (
          <span className="text-xs font-bold text-emerald-600 flex items-center gap-1">
            <CheckCircle size={11} /> 풀이 완료
          </span>
        )}
      </div>
      <p className="text-sm text-slate-800 font-medium whitespace-pre-line mb-3">{practice.q}</p>
      <div className="flex gap-2 flex-wrap">
        <button onClick={() => setShowHint(!showHint)}
          className="text-xs px-3 py-1.5 rounded-full bg-amber-100 text-amber-700 hover:bg-amber-200 transition-colors flex items-center gap-1">
          <Lightbulb size={11} /> 힌트
        </button>
        <button onClick={() => setShowSteps(!showSteps)}
          className="text-xs px-3 py-1.5 rounded-full bg-blue-100 text-blue-700 hover:bg-blue-200 transition-colors flex items-center gap-1">
          <PenTool size={11} /> {showSteps ? '풀이 접기' : '단계별 풀이'}
        </button>
        <button onClick={handleShowAnswer}
          className="text-xs px-3 py-1.5 rounded-full bg-emerald-100 text-emerald-700 hover:bg-emerald-200 transition-colors flex items-center gap-1">
          <CheckCircle size={11} /> {showAnswer ? '정답 확인 중' : '정답 보기'}
        </button>
      </div>
      {showHint && (
        <div className="mt-3 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-xs text-amber-700">
          💡 <span className="font-medium">{practice.hint}</span>
        </div>
      )}
      {showSteps && (
        <div className="mt-3 bg-blue-50 border border-blue-200 rounded-xl p-3">
          <p className="text-xs font-bold text-blue-700 mb-2 flex items-center gap-1.5">
            <PenTool size={11} /> 단계별 풀이 과정
          </p>
          <div className="space-y-2">
            {solutionSteps.map((step, i) => (
              <div key={i} className="flex gap-2">
                <div className="w-5 h-5 bg-blue-500 text-white rounded-full text-xs font-black flex items-center justify-center flex-shrink-0 mt-0.5">
                  {i + 1}
                </div>
                <div className="flex-1">
                  <p className="text-xs font-bold text-slate-600 mb-0.5">{step.label}</p>
                  <div className="bg-white rounded-lg px-2 py-1.5 border border-blue-100">
                    <p className="text-xs text-slate-700 whitespace-pre-line">{step.content}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {showAnswer && (
        <div className="mt-3 bg-emerald-50 border-2 border-emerald-300 rounded-xl px-3 py-2.5">
          <p className="text-xs font-bold text-emerald-600 mb-1">✅ 정답</p>
          <p className="text-sm font-bold text-emerald-800 whitespace-pre-line">{practice.a}</p>
        </div>
      )}
    </div>
  )
}

// ── 예시 컴포넌트 ─────────────────────────────────────────────────────────────
function ExampleCard({ ex, idx }: { ex: { q: string; a: string; detail: string }; idx: number }) {
  const [open, setOpen] = useState(false)
  return (
    <div className="border border-slate-200 rounded-xl bg-white overflow-hidden">
      <div className="p-3">
        <p className="text-xs font-bold text-slate-400 mb-1">예시 {idx + 1}</p>
        <p className="text-sm text-slate-800 whitespace-pre-line">{ex.q}</p>
        <div className="mt-2 bg-primary-50 border border-primary-200 rounded-lg px-3 py-2">
          <p className="text-sm font-bold text-primary-700">💡 {ex.a}</p>
        </div>
      </div>
      {ex.detail && (
        <button onClick={() => setOpen(!open)}
          className="w-full text-left px-3 py-2 bg-slate-50 border-t border-slate-100 text-xs text-slate-500 hover:bg-slate-100 transition-colors flex items-center gap-1">
          <ArrowRight size={10} className={`transition-transform ${open ? 'rotate-90' : ''}`} />
          {open ? '풀이 숨기기' : '상세 풀이 보기'}
        </button>
      )}
      {open && ex.detail && (
        <div className="px-3 pb-3 bg-slate-50 text-xs text-slate-600 leading-relaxed whitespace-pre-line">
          {ex.detail}
        </div>
      )}
    </div>
  )
}

// ── 메인 페이지 ───────────────────────────────────────────────────────────────
const UNIT_ID = 'F2-2'

export default function F2_2LearnPage({ defaultStepId }: { defaultStepId?: string | null }) {
  const [selectedId, setSelectedId] = useState<string | null>(defaultStepId ?? null)
  const [videoModal, setVideoModal] = useState<{ stepId: string; label: string } | null>(null)
  const getApprovedByTag   = useVideoStore(s => s.getApprovedByTag)
  const getRecruitingByTag = useVideoStore(s => s.getRecruitingByTag)
  const { completedCount, steps: stepProgress, pct } = useLearnProgress(UNIT_ID)
  const { completeStep, init } = useProgressStore(s => ({ completeStep: s.completeStep, init: s.init }))

  useEffect(() => { init('student_001') }, [])

  useEffect(() => {
    if (defaultStepId) {
      setSelectedId(defaultStepId)
      setTimeout(() => {
        const el = document.getElementById(`step-${defaultStepId}`)
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }, 300)
    }
  }, [defaultStepId])

  const selected = STEPS.find(s => s.id === selectedId) ?? null
  const selectedIdx = STEPS.findIndex(s => s.id === selectedId)

  const isStepCompleted = (id: string) =>
    stepProgress[id]?.status === 'completed' || stepProgress[id]?.status === 'mastered'
  const isStepPracticed = (id: string) => stepProgress[id]?.practiceAnswered ?? false

  const handleComplete = (stepId: string) => {
    const step = STEPS.find(s => s.id === stepId)
    completeStep(UNIT_ID, stepId, step?.title)
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
          <BookOpen size={24} className="text-indigo-500" />
          학습 단계
        </h1>
        <p className="text-sm text-slate-500 mt-1">10단계 체계적 학습 경로 — 일차방정식과 직선 → 종합 오답 분석</p>
      </div>

      {/* Progress Bar */}
      <div className="card mb-6">
        <div className="flex justify-between text-sm mb-2">
          <span className="font-semibold text-slate-700">학습 진도</span>
          <span className="text-indigo-600 font-bold">{completedCount} / 10 단계 완료</span>
        </div>
        <div className="bg-slate-100 rounded-full h-3">
          <div
            className="bg-gradient-to-r from-indigo-500 to-violet-500 h-3 rounded-full transition-all duration-700"
            style={{ width: `${pct}%` }}
          />
        </div>
        <div className="flex justify-between text-xs text-slate-400 mt-1.5">
          <span>시작</span>
          <span className="font-semibold text-indigo-500">{pct}%</span>
          <span>완료</span>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Step List */}
        <div className="lg:col-span-1 space-y-2">
          {STEPS.map((step) => {
            const isActive = step.id === selectedId
            const completed = isStepCompleted(step.id)
            const practiced = isStepPracticed(step.id)
            const diffConf = DIFFICULTY_CONFIG[step.difficulty]

            return (
              <button
                key={step.id}
                id={`step-${step.id}`}
                onClick={() => setSelectedId(prev => prev === step.id ? null : step.id)}
                className={`w-full text-left p-3 rounded-xl border-2 transition-all ${
                  isActive
                    ? `${step.color.border} ${step.color.bg} shadow-md`
                    : completed
                      ? 'border-emerald-200 bg-emerald-50/50 hover:bg-emerald-50'
                      : 'border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 text-lg ${
                    completed ? 'bg-emerald-500' : isActive ? step.color.btn + ' opacity-90' : 'bg-slate-100'
                  }`}>
                    {completed
                      ? <CheckCircle size={18} className="text-white" />
                      : <span>{step.icon}</span>}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-bold text-slate-400">{step.id}</p>
                    <p className={`text-sm font-semibold truncate ${isActive ? step.color.accent : 'text-slate-700'}`}>
                      {step.title}
                    </p>
                    <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                      <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${diffConf.color}`}>
                        {diffConf.label}
                      </span>
                      {practiced && !completed && (
                        <span className="text-xs px-1.5 py-0.5 rounded-full font-medium bg-blue-100 text-blue-600">연습완료</span>
                      )}
                    </div>
                  </div>
                  <ChevronRight
                    size={14}
                    className={`flex-shrink-0 transition-transform ${isActive ? 'rotate-90 ' + step.color.accent : 'text-slate-300'}`}
                  />
                </div>
              </button>
            )
          })}
        </div>

        {/* Step Detail */}
        <div className="lg:col-span-2">
          {!selected ? (
            <div className="card flex flex-col items-center justify-center py-20 text-center">
              <div className="text-6xl mb-4">🔗</div>
              <p className="text-lg font-bold text-slate-600 mb-1">학습 단계를 선택하세요</p>
              <p className="text-sm text-slate-400">왼쪽 목록에서 원하는 단계를 클릭하면<br />개념 설명과 예시, 연습 문제가 나타납니다.</p>
              {completedCount > 0 && (
                <div className="mt-4 px-4 py-2.5 bg-emerald-50 border border-emerald-200 rounded-xl">
                  <p className="text-sm font-semibold text-emerald-700">
                    🎉 {completedCount}단계 완료! 계속 학습하세요.
                  </p>
                </div>
              )}
              <div className="mt-4 flex flex-wrap gap-2 justify-center">
                {STEPS.slice(0, 4).map(s => (
                  <button
                    key={s.id}
                    onClick={() => setSelectedId(s.id)}
                    className="text-xs px-3 py-1.5 rounded-full border border-slate-200 hover:bg-slate-50 transition-colors text-slate-600"
                  >
                    {s.icon} {s.title}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Header Card */}
              <div className={`rounded-2xl border-2 ${selected.color.border} ${selected.color.bg} p-5`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <span className="text-4xl">{selected.icon}</span>
                    <div>
                      <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                        <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${selected.color.badge}`}>{selected.id}</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${DIFFICULTY_CONFIG[selected.difficulty].color}`}>
                          {DIFFICULTY_CONFIG[selected.difficulty].label}
                        </span>
                        {isStepCompleted(selected.id) && (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-bold flex items-center gap-1">
                            <CheckCircle size={10} /> 완료
                          </span>
                        )}
                      </div>
                      <h2 className="text-xl font-black text-slate-800">{selected.title}</h2>
                      <p className="text-sm text-slate-500 mt-0.5">{selected.subtitle}</p>
                    </div>
                  </div>
                </div>
                <div className="mt-3 p-3 bg-white/70 rounded-xl">
                  <p className="text-xs font-semibold text-slate-500 mb-1 flex items-center gap-1">
                    <Target size={11} /> 학습 목표
                  </p>
                  <p className="text-sm text-slate-700 leading-relaxed">{selected.objective}</p>
                </div>
                {selected.teaches.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {selected.teaches.map(c => (
                      <span key={c} className={`text-xs px-2 py-0.5 rounded-full font-mono font-medium ${selected.color.badge}`}>
                        {c}
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Concept */}
              <div className="card">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-bold text-slate-800 flex items-center gap-2">
                    <Lightbulb size={16} className="text-yellow-500" />
                    개념 설명
                  </h3>
                  {(() => {
                    const approved   = getApprovedByTag(selected.id)
                    const recruiting = getRecruitingByTag(selected.id)
                    if (approved.length > 0) {
                      return (
                        <button
                          onClick={() => setVideoModal({ stepId: selected.id, label: selected.title })}
                          className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-xl font-bold transition-all bg-red-500 hover:bg-red-600 text-white shadow-sm"
                        >
                          <Youtube size={12} />
                          영상으로 학습 ({approved.length})
                        </button>
                      )
                    }
                    if (recruiting.length > 0) {
                      return (
                        <button
                          onClick={() => setVideoModal({ stepId: selected.id, label: selected.title })}
                          className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-xl font-bold transition-all bg-violet-100 hover:bg-violet-200 text-violet-700 border border-violet-300"
                        >
                          <Youtube size={12} />
                          🎬 영상 모집 중
                        </button>
                      )
                    }
                    return (
                      <button
                        onClick={() => setVideoModal({ stepId: selected.id, label: selected.title })}
                        className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-xl font-bold transition-all bg-slate-100 hover:bg-slate-200 text-slate-400 border border-slate-200"
                      >
                        <Youtube size={12} />
                        영상 학습
                      </button>
                    )
                  })()}
                </div>
                <div className="bg-slate-50 rounded-xl p-4">
                  <pre className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap font-sans">
                    {selected.concept}
                  </pre>
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  {selected.keyPoints.map(p => (
                    <span key={p} className="text-xs bg-primary-100 text-primary-700 px-2.5 py-1 rounded-full font-medium flex items-center gap-1">
                      <Star size={9} /> {p}
                    </span>
                  ))}
                </div>
              </div>

              {/* Examples */}
              <div className="card">
                <h3 className="font-bold text-slate-800 mb-3 flex items-center gap-2">
                  <Calculator size={16} className="text-blue-500" />
                  예시 문제
                </h3>
                <div className="space-y-3">
                  {selected.examples.map((ex, i) => (
                    <ExampleCard key={i} ex={ex} idx={i} />
                  ))}
                </div>
              </div>

              {/* Practice */}
              <PracticeBox
                practice={selected.practice}
                stepId={selected.id}
                unitId={UNIT_ID}
                alreadyAnswered={isStepPracticed(selected.id)}
                onAnswered={() => {}}
              />

              {/* Misconceptions */}
              {selected.misconceptions.length > 0 && (
                <div className="rounded-xl border-2 border-red-200 bg-red-50 p-4">
                  <h3 className="font-bold text-red-700 text-sm mb-3 flex items-center gap-2">
                    <AlertTriangle size={14} /> 자주 하는 실수 (오개념 주의!)
                  </h3>
                  <div className="space-y-2">
                    {selected.misconceptions.map(m => (
                      <div key={m.code} className="flex items-start gap-2 bg-white rounded-lg px-3 py-2 border border-red-100">
                        <span className="text-xs font-black text-red-500 bg-red-100 px-1.5 py-0.5 rounded flex-shrink-0">{m.code}</span>
                        <span className="text-xs text-slate-700">{m.desc}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Complete Button */}
              {!isStepCompleted(selected.id) ? (
                <button
                  onClick={() => handleComplete(selected.id)}
                  className={`w-full py-3 rounded-2xl text-white font-black text-sm flex items-center justify-center gap-2 transition-all shadow-md hover:shadow-lg ${selected.color.btn}`}
                >
                  <CheckCircle size={16} /> 이 단계 학습 완료!
                </button>
              ) : (
                <div className="bg-emerald-50 border-2 border-emerald-300 rounded-2xl p-3 flex items-center gap-3">
                  <CheckCircle size={20} className="text-emerald-500 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-black text-emerald-700">완료된 단계입니다 🎉</p>
                    <p className="text-xs text-emerald-500">완료일: {stepProgress[selected.id]?.completedAt?.slice(0,10) ?? ''}</p>
                  </div>
                </div>
              )}

              {/* Navigation */}
              <div className="flex justify-between items-center pt-2">
                {selectedIdx > 0 ? (
                  <button
                    onClick={() => setSelectedId(STEPS[selectedIdx - 1].id)}
                    className="text-sm text-slate-500 hover:text-slate-700 flex items-center gap-1 transition-colors"
                  >
                    ← 이전 단계
                  </button>
                ) : <span />}
                {selectedIdx < STEPS.length - 1 ? (
                  <button
                    onClick={() => setSelectedId(STEPS[selectedIdx + 1].id)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-xl text-white text-sm font-semibold transition-all ${selected.color.btn}`}
                  >
                    다음 단계 <ArrowRight size={14} />
                  </button>
                ) : (
                  <span className="text-sm font-bold text-emerald-600 flex items-center gap-1">
                    <CheckCircle size={14} /> 전 과정 완료!
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {videoModal && (
        <ConceptVideoModal
          conceptTag={videoModal.stepId}
          conceptLabel={videoModal.label}
          viewerId="student_001"
          onClose={() => setVideoModal(null)}
          onCompleted={() => setVideoModal(null)}
        />
      )}
    </div>
  )
}
